import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { DatePickerField } from '@/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    optionName,
    UNIT_LABEL_KEY,
    UNIT_OPTIONS,
    VAT_LABEL_KEY,
    VAT_OPTIONS,
    type ImportLineDraft,
} from '../manualImportMeta';
import type { ManualImportForm } from '../useManualImport';
import { LabeledNumberField } from './LabeledNumberField';
import { LabeledSelect } from './LabeledSelect';
import { LabeledTextField } from './LabeledTextField';
import { LineVariationFields } from './LineVariationFields';
import { ReadOnlyField } from './ReadOnlyField';

type ProductLineRowProps = {
    form: ManualImportForm;
    line: ImportLineDraft;
};

/** One product line. Identity + catalog + variation are editable for a new
 *  product and read-only (prefilled from the picked variant) for a restock; the
 *  quantity/cost/price numerics, optional min-quantity and dates are editable in
 *  both modes. */
export const ProductLineRow: React.FC<ProductLineRowProps> = ({
    form,
    line,
}) => {
    const { t } = useTranslation('pages');
    const isNew = line.mode === 'new';
    const isMarkupMode = form.state.pricingMode === 'markup';

    return (
        <div
            className="flex flex-col gap-3 rounded-lg bg-surface-secondary p-3"
            {...getNodeTestIdProp(`manual-import-line-${line.key}`)}
        >
            <div
                className="flex items-center justify-between gap-3"
                {...getNodeTestIdProp(`manual-import-line-header-${line.key}`)}
            >
                <span className="text-xs font-medium text-muted">
                    {isNew
                        ? t('purchases.manualImport.addNewProduct')
                        : t('purchases.manualImport.product')}
                </span>
                <Button
                    isIconOnly
                    onPress={() => form.onRemoveLine(line.key)}
                    variant="ghost"
                    {...getNodeTestIdProp(
                        `manual-import-remove-line-${line.key}`,
                    )}
                >
                    <Icon icon="gravity-ui:trash-bin" />
                </Button>
            </div>

            {/* Identity: editable for new products, read-only for a restock. */}
            {isNew ? (
                <div
                    className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                    {...getNodeTestIdProp(
                        `manual-import-line-identity-${line.key}`,
                    )}
                >
                    <LabeledTextField
                        label={t('purchases.manualImport.productName')}
                        onChange={(value) =>
                            form.onLineFieldChange(line.key, 'name', value)
                        }
                        slot={`line-name-${line.key}`}
                        value={line.name}
                    />
                    <LabeledTextField
                        label={t('purchases.manualImport.sku')}
                        onChange={(value) =>
                            form.onLineFieldChange(line.key, 'sku', value)
                        }
                        slot={`line-sku-${line.key}`}
                        value={line.sku}
                    />
                    <LabeledTextField
                        label={t('purchases.manualImport.barcode')}
                        onChange={(value) =>
                            form.onLineFieldChange(line.key, 'barcode', value)
                        }
                        slot={`line-barcode-${line.key}`}
                        value={line.barcode}
                    />
                </div>
            ) : (
                <div
                    className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                    {...getNodeTestIdProp(
                        `manual-import-line-identity-${line.key}`,
                    )}
                >
                    <ReadOnlyField
                        label={t('purchases.manualImport.productName')}
                        slot={`line-name-${line.key}`}
                        value={line.name}
                    />
                    <ReadOnlyField
                        label={t('purchases.manualImport.sku')}
                        slot={`line-sku-${line.key}`}
                        value={line.sku}
                    />
                    <ReadOnlyField
                        label={t('purchases.manualImport.barcode')}
                        slot={`line-barcode-${line.key}`}
                        value={line.barcode}
                    />
                </div>
            )}

            {/* Catalog: category / brand / unit / VAT. */}
            {isNew ? (
                <div
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
                    {...getNodeTestIdProp(
                        `manual-import-line-catalog-${line.key}`,
                    )}
                >
                    <LabeledSelect
                        label={t('purchases.manualImport.category')}
                        onChange={(value) =>
                            form.onLineCategoryChange(line.key, value || null)
                        }
                        options={form.categoryOptions}
                        placeholder={t(
                            'purchases.manualImport.categoryPlaceholder',
                        )}
                        slot={`line-category-${line.key}`}
                        value={line.categoryId ?? ''}
                    />
                    <LabeledSelect
                        label={t('purchases.manualImport.brand')}
                        onChange={(value) =>
                            form.onLineBrandChange(line.key, value || null)
                        }
                        options={form.brandOptions}
                        placeholder={t(
                            'purchases.manualImport.brandPlaceholder',
                        )}
                        slot={`line-brand-${line.key}`}
                        value={line.brandId ?? ''}
                    />
                    <LabeledSelect
                        label={t('purchases.manualImport.unit')}
                        onChange={(value) =>
                            form.onLineUnitChange(
                                line.key,
                                value as (typeof UNIT_OPTIONS)[number],
                            )
                        }
                        options={UNIT_OPTIONS.map((unit) => ({
                            id: unit,
                            name: t(UNIT_LABEL_KEY[unit]),
                        }))}
                        slot={`line-unit-${line.key}`}
                        value={line.unit}
                    />
                    <LabeledSelect
                        label={t('purchases.manualImport.vat')}
                        onChange={(value) =>
                            form.onLineVatChange(
                                line.key,
                                value as (typeof VAT_OPTIONS)[number],
                            )
                        }
                        options={VAT_OPTIONS.map((vat) => ({
                            id: vat,
                            name: t(VAT_LABEL_KEY[vat]),
                        }))}
                        slot={`line-vat-${line.key}`}
                        value={line.vatType}
                    />
                </div>
            ) : (
                <div
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
                    {...getNodeTestIdProp(
                        `manual-import-line-catalog-${line.key}`,
                    )}
                >
                    <ReadOnlyField
                        label={t('purchases.manualImport.category')}
                        slot={`line-category-${line.key}`}
                        value={optionName(
                            form.categoryOptions,
                            line.categoryId,
                        )}
                    />
                    <ReadOnlyField
                        label={t('purchases.manualImport.brand')}
                        slot={`line-brand-${line.key}`}
                        value={optionName(form.brandOptions, line.brandId)}
                    />
                    <ReadOnlyField
                        label={t('purchases.manualImport.unit')}
                        slot={`line-unit-${line.key}`}
                        value={t(UNIT_LABEL_KEY[line.unit])}
                    />
                    <ReadOnlyField
                        label={t('purchases.manualImport.vat')}
                        slot={`line-vat-${line.key}`}
                        value={t(VAT_LABEL_KEY[line.vatType])}
                    />
                </div>
            )}

            {/* Editable numerics — shared by both modes. */}
            <div
                className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                {...getNodeTestIdProp(
                    `manual-import-line-numerics-${line.key}`,
                )}
            >
                <LabeledNumberField
                    label={t('purchases.manualImport.quantity')}
                    minValue={0}
                    onChange={(value) =>
                        form.onLineFieldChange(line.key, 'quantity', value)
                    }
                    slot={`line-quantity-${line.key}`}
                    value={line.quantity}
                />
                <LabeledNumberField
                    currency
                    label={t('purchases.manualImport.purchasePrice')}
                    onChange={(value) =>
                        form.onLineFieldChange(line.key, 'purchasePrice', value)
                    }
                    slot={`line-cost-${line.key}`}
                    value={line.purchasePrice}
                />
                {/* Sale price ⇄ markup: the pricing-mode toggle decides which
                    one is typed and which one is derived (read-only). */}
                <LabeledNumberField
                    currency
                    label={t('purchases.manualImport.salePrice')}
                    onChange={(value) =>
                        form.onLineFieldChange(line.key, 'salePrice', value)
                    }
                    readOnly={isMarkupMode}
                    slot={`line-sale-${line.key}`}
                    value={line.salePrice}
                />
                {/* Markup may go negative (below-cost pricing) down to -100%,
                    the floor where the derived sale price reaches zero. */}
                <LabeledNumberField
                    label={t('purchases.manualImport.markup')}
                    minValue={-100}
                    onChange={(value) =>
                        form.onLineFieldChange(line.key, 'markup', value)
                    }
                    percent
                    readOnly={!isMarkupMode}
                    slot={`line-markup-${line.key}`}
                    value={line.markup}
                />
                <LabeledNumberField
                    currency
                    label={t('purchases.manualImport.wholesalePrice')}
                    onChange={(value) =>
                        form.onLineFieldChange(
                            line.key,
                            'wholesalePrice',
                            value,
                        )
                    }
                    slot={`line-wholesale-${line.key}`}
                    value={line.wholesalePrice}
                />
                <LabeledNumberField
                    label={t('purchases.manualImport.minimumQuantity')}
                    minValue={0}
                    onChange={(value) =>
                        form.onLineFieldChange(
                            line.key,
                            'minimumQuantity',
                            value,
                        )
                    }
                    slot={`line-min-quantity-${line.key}`}
                    value={line.minimumQuantity}
                />
            </div>

            <div
                className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                {...getNodeTestIdProp(`manual-import-line-dates-${line.key}`)}
            >
                <DatePickerField
                    id={`manual-import-line-expiry-${line.key}`}
                    label={t('purchases.manualImport.expiryDate')}
                    name={`manual-import-line-expiry-${line.key}`}
                    onChange={(value) =>
                        form.onLineFieldChange(line.key, 'expiryDate', value)
                    }
                    value={line.expiryDate}
                />
                <DatePickerField
                    id={`manual-import-line-sale-due-${line.key}`}
                    label={t('purchases.manualImport.saleDueDate')}
                    name={`manual-import-line-sale-due-${line.key}`}
                    onChange={(value) =>
                        form.onLineFieldChange(line.key, 'saleDueDate', value)
                    }
                    value={line.saleDueDate}
                />
            </div>

            {/* Product attributes last — secondary to identity/pricing, and
                collapsed by default behind the Disclosure. */}
            <LineVariationFields form={form} line={line} />
        </div>
    );
};
